import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Note: localStorage is used for token storage. For enhanced security in production,
// consider using httpOnly cookies with proper CSRF protection.

export function AuthProvider({ children }) {
    const [token, setToken] = useState(() => localStorage.getItem('admin_token'));
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);

    const verifyToken = useCallback(async () => {
        if (!token) {
            setIsAuthenticated(false);
            setLoading(false);
            return;
        }

        try {
            await axios.get(`${API}/auth/verify`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIsAuthenticated(true);
        } catch (error) {
            console.warn('Token verification failed:', error?.response?.status || 'Network error');
            localStorage.removeItem('admin_token');
            setToken(null);
            setIsAuthenticated(false);
        }
        setLoading(false);
    }, [token]);

    useEffect(() => {
        verifyToken();
    }, [verifyToken]);

    const login = useCallback(async (password) => {
        const response = await axios.post(`${API}/auth/login`, { password });
        const newToken = response.data.token;
        localStorage.setItem('admin_token', newToken);
        setToken(newToken);
        setIsAuthenticated(true);
        return response.data;
    }, []);

    const logout = useCallback(async () => {
        try {
            await axios.post(`${API}/auth/logout`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
        } catch (error) {
            // Log but don't block logout on server errors
            console.warn('Logout request failed:', error?.response?.status || 'Network error');
        }
        localStorage.removeItem('admin_token');
        setToken(null);
        setIsAuthenticated(false);
    }, [token]);

    const contextValue = useMemo(() => ({
        token,
        isAuthenticated,
        loading,
        login,
        logout
    }), [token, isAuthenticated, loading, login, logout]);

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
